import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Send,
  Download,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/DataTable";
import { AttachmentPicker } from "@/components/AttachmentPicker";
import {
  ApiError,
  addTicketMessage,
  getTicket,
  type TicketAttachment,
  type TicketMessage,
  type TicketPriority,
  type TicketStatus,
} from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: "Basse",
  medium: "Moyenne",
  high: "Haute",
  urgent: "Urgente",
};

const PRIORITY_COLORS: Record<TicketPriority, "info" | "success" | "warning" | "danger"> = {
  low: "info",
  medium: "success",
  high: "warning",
  urgent: "danger",
};

const STATUS_COLORS: Record<TicketStatus, "info" | "success" | "warning" | "danger"> = {
  open: "info",
  in_progress: "warning",
  resolved: "success",
  closed: "success",
};

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Ouvert",
  in_progress: "En cours",
  resolved: "Résolu",
  closed: "Fermé",
};

export function SupportDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["tickets", "detail", ticketId],
    queryFn: () => getTicket(ticketId!, { mark_viewed: true }),
    enabled: !!ticketId,
  });

  const mutation = useMutation({
    mutationFn: () =>
      addTicketMessage(ticketId!, {
        message: reply.trim(),
        attachments: attachments.length > 0 ? attachments : undefined,
      }),
    onSuccess: () => {
      setReply("");
      setAttachments([]);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError(err instanceof Error ? err.message : "Erreur");
    },
  });

  if (query.isLoading) {
    return <div className="card card-body text-[#7e8299]">Chargement…</div>;
  }
  if (query.error || !query.data) {
    return (
      <div className="card card-body text-danger-700">
        {query.error instanceof ApiError
          ? query.error.detail
          : "Ticket introuvable"}
      </div>
    );
  }

  const ticket = query.data;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    mutation.mutate();
  };

  return (
    <div>
      <PageHeader
        title={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="truncate max-w-[500px]">{ticket.subject}</span>
            <Badge color={STATUS_COLORS[ticket.status]}>
              {STATUS_LABELS[ticket.status]}
            </Badge>
            <Badge color={PRIORITY_COLORS[ticket.priority]}>
              {PRIORITY_LABELS[ticket.priority]}
            </Badge>
          </div>
        }
        breadcrumbs={[
          { label: "Mon espace" },
          { label: "Support", to: "/support" },
          { label: ticket.ticket_id },
        ]}
        actions={
          <button onClick={() => navigate(-1)} className="btn btn-light btn-sm">
            <ArrowLeft size={14} />
            Retour
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="lg:col-span-3 space-y-5">
          <div className="card">
            <div className="card-body space-y-4">
              {ticket.messages.length === 0 && (
                <div className="text-sm text-[#7e8299] text-center py-6">
                  Aucun message pour l'instant.
                </div>
              )}
              {ticket.messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
            </div>
          </div>

          {ticket.status !== "closed" && (
            <div className="card">
              <div className="card-header">
                <div className="card-title text-sm">Répondre</div>
              </div>
              <div className="card-body">
                <form onSubmit={submit} className="space-y-4">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    required
                    placeholder="Écrivez votre réponse à l'équipe support…"
                    className="form-input min-h-[120px] resize-y"
                    disabled={mutation.isPending}
                  />
                  <AttachmentPicker
                    value={attachments}
                    onChange={setAttachments}
                    disabled={mutation.isPending}
                  />
                  {error && (
                    <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">
                      {error}
                    </div>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={mutation.isPending || !reply.trim()}
                      className="btn btn-primary btn-sm"
                    >
                      <Send size={13} />
                      {mutation.isPending ? "Envoi…" : "Envoyer"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {ticket.status === "closed" && (
            <div className="card card-body text-sm text-[#7e8299] text-center">
              Ce ticket est fermé.{" "}
              <Link to="/support" className="text-brand-700 hover:underline">
                Ouvrir un nouveau ticket
              </Link>
              {" "}si nécessaire.
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-header">
              <div className="card-title text-sm">Détails</div>
            </div>
            <div className="card-body space-y-3 text-sm">
              <Field label="Référence" value={ticket.ticket_id} mono />
              <Field label="Catégorie" value={ticket.category} />
              <Field label="Créé par" value={ticket.created_by} />
              <Field label="Créé le" value={formatDate(ticket.created_at)} />
              {ticket.updated_at && (
                <Field label="Dernière MAJ" value={formatRelative(ticket.updated_at)} />
              )}
              <Field
                label="Messages"
                value={String(ticket.message_count)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function MessageBubble({ message }: { message: TicketMessage }) {
  const isStaff = message.sender_type === "staff";
  return (
    <div
      className={`flex gap-3 ${isStaff ? "flex-row" : "flex-row-reverse"}`}
    >
      <div
        className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-xs font-bold ${
          isStaff
            ? "bg-[#f5f8fa] text-[#3f4254]"
            : "bg-brand-500 text-white"
        }`}
      >
        {(message.sender_name || (isStaff ? "M" : "?"))[0].toUpperCase()}
      </div>
      <div className={`min-w-0 max-w-[75%] ${isStaff ? "" : "text-right"}`}>
        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
          <div className="text-xs font-bold text-[#181c32]">
            {message.sender_name || (isStaff ? "Support Moko" : "Vous")}
          </div>
          {isStaff && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brand-50 text-brand-700">
              Support Moko
            </span>
          )}
          <div className="text-[10px] text-[#a1a5b7]" title={formatDate(message.created_at)}>
            {formatRelative(message.created_at)}
          </div>
        </div>
        <div
          className={`rounded-lg px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isStaff
              ? "bg-[#f5f8fa] text-[#181c32]"
              : "bg-brand-50 text-[#181c32]"
          }`}
        >
          {message.message}
        </div>
        {message.attachments && message.attachments.length > 0 && (
          <div className={`mt-2 space-y-1.5 ${isStaff ? "" : "flex flex-col items-end"}`}>
            {message.attachments.map((att, i) => (
              <AttachmentDownload key={i} attachment={att} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentDownload({ attachment }: { attachment: TicketAttachment }) {
  const isImage = attachment.type.startsWith("image/");
  return (
    <a
      href={attachment.data_url}
      download={attachment.name}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#eff2f5] hover:bg-[#f9fafb] text-xs max-w-full"
    >
      {isImage ? (
        <ImageIcon size={13} className="text-brand-700 shrink-0" />
      ) : (
        <FileText size={13} className="text-[#5e6278] shrink-0" />
      )}
      <span className="text-[#181c32] font-medium truncate">{attachment.name}</span>
      <Download size={11} className="text-[#7e8299] shrink-0" />
    </a>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-1">
        {label}
      </div>
      <div className={`text-sm text-[#181c32] ${mono ? "font-mono break-all" : ""}`}>{value}</div>
    </div>
  );
}
